// BuildingFloors_Pure.ts — PURE engine for the Buildings Set1 level-stacking layer (§3.5).
// No mobx-graphlink / three / DOM / MapObject imports — the Node vitest lane imports this
// directly. The DB-dependent "collect + resolve claimants" half lives in `BuildingAccessors.ts`;
// this file does the ordering, conflict resolution, gap bridging, and Z stacking that is
// independent of the store.
import type {FloorSlot} from "./BuildingGeometry.js";

/** One floor-number claimant (a `Mod_Level`'s desired floor number, attached to its owning
 *  object). `orderKey` is the object's `orderKey` string; the LEXICOGRAPHICALLY LAST key wins a
 *  conflict (§3.5 "last claimant wins", matching how `OrderKey`/`orderKey` sorts object order).
 *  `resolveHeight` returns the floor's storey height fields (thickness + wall height); it is a
 *  function so the pure engine stays free of the accessor layer (the caller injects
 *  `ResolveLevelParams`). */
export type FloorClaim = {
	floorNumber: number;
	levelObjID: string;
	orderKey: string|n;
	/** The resolved storey height for this claimant (through the fallback chain). */
	resolveHeight: () => {floorThickness: number, wallHeight: number};
};

export type ResolveBuildingFloors_Result = {
	/** Claimed + gap-bridged floors, keyed by floor number. */
	floors: Map<number, FloorSlot>;
	/** Human-readable warnings for the UI diagnostics section. */
	warnings: string[];
};

const DEFAULT_STOREY_FALLBACK = {floorThickness: 0.25, wallHeight: 2.7};

/**
 * Build the ordered `FloorSlot` list from a set of floor-number claims, per §3.5:
 *  1. Sort all claims by `orderKey` ascending (so later = lexicographically larger = "last").
 *  2. For each floorNumber, the LAST claimant in that sorted order wins; earlier ones land in the
 *     winner's `shadowedBy` and produce a warning.
 *  3. Resolve each winning claim's storey height (thickness + wallHeight).
 *  4. Stack in building-local Z. Floor 1's WALKABLE SURFACE (`floorTopZ`) is the origin (z = 0),
 *     matching the building object's world-Z (the renderer places the object's z at floor 1's
 *     `floorTopZ`). So `baseZ(1) = −thickness(1)` — the slab extends DOWN from the object's z —
 *     and `wallTopZ(1) = wallHeight(1)`. Ascending: floorTopZ(n+1) = wallTopZ(n).
 *       Descending (basements): baseZ(-1) = floor1SlabBottom − (thickness(-1) + wallHeight(-1));
 *       baseZ(-2) = baseZ(-1) − (thickness(-2) + wallHeight(-2)); etc.
 *  5. Gaps in the claimed set (e.g. 1,2,5) are bridged using the fallback storey height of the
 *     NEAREST claimed floor (the winning claimant whose height is reused), and warned about.
 *
 * Negative floor numbers are basements and stack downward from floor 1's slab bottom. Floor 0 is
 * invalid and is dropped (with a warning). The function does not assume `claims` is pre-filtered.
 */
export function ResolveBuildingFloors_Pure(claims: FloorClaim[], fallbackStorey = DEFAULT_STOREY_FALLBACK): ResolveBuildingFloors_Result {
	const warnings: string[] = [];

	// Drop invalid floor numbers (0, and non-integers).
	const validClaims: FloorClaim[] = [];
	for (const c of claims) {
		if (c.floorNumber == 0) {
			warnings.push(`Level "${c.levelObjID}" claims floor 0, which is invalid (basements are -1, -2, ...). Dropped.`);
			continue;
		}
		if (!Number.isInteger(c.floorNumber)) {
			warnings.push(`Level "${c.levelObjID}" claims non-integer floor ${c.floorNumber}. Dropped.`);
			continue;
		}
		validClaims.push(c);
	}

	// Group by floor number; within a group, the LAST (max orderKey) claimant wins.
	const winners = new Map<number, {claim: FloorClaim, shadowed: FloorClaim[]}>();
	const sortedByOrder = [...validClaims].sort((a, b)=>{
		const ak = a.orderKey ?? "";
		const bk = b.orderKey ?? "";
		if (ak == bk) return a.levelObjID < b.levelObjID ? -1 : a.levelObjID > b.levelObjID ? 1 : 0;
		return ak < bk ? -1 : 1;
	});
	for (const claim of sortedByOrder) {
		const existing = winners.get(claim.floorNumber);
		if (existing == null) {
			winners.set(claim.floorNumber, {claim, shadowed: []});
		} else {
			// Later claimant (larger orderKey) wins; the earlier one becomes shadowed.
			const earlier = existing.claim;
			existing.claim = claim;
			existing.shadowed.push(earlier);
		}
	}

	// Build the winning slots.
	const winningFloors: Array<{floorNumber: number, claim: FloorClaim, shadowed: FloorClaim[]}> = [];
	for (const [floorNumber, entry] of winners) {
		winningFloors.push({floorNumber, claim: entry.claim, shadowed: entry.shadowed});
		for (const shadowed of entry.shadowed) {
			warnings.push(`Floor ${floorNumber}: level "${shadowed.levelObjID}" is shadowed by "${entry.claim.levelObjID}" (higher orderKey).`);
		}
	}
	winningFloors.sort((a, b)=>a.floorNumber - b.floorNumber);

	// Stacking in building-local Z.
	const floors = new Map<number, FloorSlot>();
	// Ascending (1, 2, 3, ...): floor 1's WALKABLE SURFACE (floorTopZ) = 0 (the building object's
	// world-z), then stack upward: floorTopZ(n+1) = wallTopZ(n).
	// Descending (-1, -2, ...): stack downward from floor 1's SLAB BOTTOM. Process -1 FIRST (it
	// sits just below floor 1), then -2 below that, etc. — so sort descending (-1, -2, -3, ...).
	const asc = winningFloors.filter(f=>f.floorNumber >= 1).sort((a, b)=>a.floorNumber - b.floorNumber);
	const desc = winningFloors.filter(f=>f.floorNumber <= -1).sort((a, b)=>b.floorNumber - a.floorNumber); // -1 first

	// Ascending pass — also bridges gaps above floor 1. `prevFloorTop` is the floorTopZ of the
	// previous (lower) floor; floor 1's floorTopZ starts at the origin (z = 0) = the object's z,
	// so floor 1's slab extends DOWN from the object's z (baseZ = −thickness) and its walls rise
	// UP from the walkable surface (wallTopZ = wallHeight).
	let prevFloorTop = 0; // floorTopZ of the previous floor (floor 1's floorTopZ = 0)
	for (let n = 1; n <= (asc.length ? asc[asc.length - 1].floorNumber : 0); n++) {
		const winning = asc.find(f=>f.floorNumber == n);
		if (winning) {
			const {floorThickness, wallHeight} = winning.claim.resolveHeight();
			const floorTopZ = prevFloorTop;
			const baseZ = floorTopZ - floorThickness;
			const wallTopZ = floorTopZ + wallHeight;
			floors.set(n, {
				floorNumber: n,
				levelObjID: winning.claim.levelObjID,
				floorThickness, wallHeight,
				baseZ,
				floorTopZ,
				wallTopZ,
				shadowedBy: winning.shadowed.map(s=>({levelObjID: s.levelObjID})),
			});
			prevFloorTop = wallTopZ;
		} else {
			// Gap: bridge using the fallback storey height of the nearest claimed floor BELOW.
			const below = asc.find(f=>f.floorNumber < n);
			const height = below ? below.claim.resolveHeight() : fallbackStorey;
			const floorTopZ = prevFloorTop;
			const baseZ = floorTopZ - height.floorThickness;
			const wallTopZ = floorTopZ + height.wallHeight;
			floors.set(n, {
				floorNumber: n,
				levelObjID: "",
				floorThickness: height.floorThickness,
				wallHeight: height.wallHeight,
				baseZ,
				floorTopZ,
				wallTopZ,
				shadowedBy: [],
			});
			prevFloorTop = wallTopZ;
			warnings.push(`Floor ${n} is not claimed by any level; bridged with the storey height of floor ${below?.floorNumber ?? 1}.`);
		}
	}

	// Descending pass — basements stack DOWNWARD below floor 1's slab bottom.
	// The top of the already-claimed downward region = floor 1's SLAB BOTTOM (baseZ(1) = 0 −
	// thickness(1)); if floor 1 isn't claimed, fall back to the origin (0).
	// baseZ(-1) = slabBottom(1) − (thickness(-1) + wallHeight(-1)); baseZ(-2) = baseZ(-1) −
	// (thickness(-2) + wallHeight(-2)); etc.
	let prevBottom = floors.get(1)?.baseZ ?? 0; // top of the region already claimed downward
	for (const f of desc) {
		const {floorThickness, wallHeight} = f.claim.resolveHeight();
		const height = floorThickness + wallHeight;
		const baseZ = prevBottom - height;
		floors.set(f.floorNumber, {
			floorNumber: f.floorNumber,
			levelObjID: f.claim.levelObjID,
			floorThickness, wallHeight,
			baseZ,
			floorTopZ: baseZ + floorThickness,
			wallTopZ: baseZ + floorThickness + wallHeight,
			shadowedBy: f.shadowed.map(s=>({levelObjID: s.levelObjID})),
		});
		prevBottom = baseZ;
	}

	return {floors, warnings};
}
